import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";

import {
  atualizarRomaneio,
  criarRomaneio,
  desvincularNotas,
  listarCidades,
  listarEstados,
  listarModais,
  listarNegocios,
  listarRotas,
  listarTiposServico,
  listarTiposVeiculo,
  obterRomaneio,
  vincularNotas,
} from "../../../services/romaneioManualApi";
import { exportCsv } from "../../../utils/exportCsv";
import {
  Aviso,
  CheckboxTodos,
  EstadoTabela,
  ModalHub,
  Pill,
  SelecaoResumo,
  extractError,
  fieldInputStyle,
  fmtBRL,
  fmtData,
  fmtNumber,
  fmtPeso,
  useLista,
  useSelecao,
} from "../_documentosFrete/comum";
import PesquisaNotasModal from "./PesquisaNotasModal";
import {
  FORM_VAZIO,
  ROTULOS,
  errosDoServidor,
  formDoRegistro,
  nomeTransportadora,
  opcoesUnidade,
  payloadDoForm,
  rotuloItem,
  rotuloRota,
  validarForm,
} from "./regras";

// Cadastro do romaneio (legado: "Criar Romaneio" / "Editar Romaneio"). A seção
// de notas só aparece depois de salvo, como no legado: "Adicionar notas" abre a
// pesquisa, "Desvincular" solta as marcadas (não as que já têm CT-e/NFse)
// depois de confirmar na própria tela.
//
// "Romaneio a partir de pedidos" (PO1-2378, épico PO1-2374): a tela de Pedidos
// abre este cadastro em modo novo com `idsNotasIniciais` e
// `idTransportadoraInicial`; ao salvar, as notas são vinculadas uma única vez.

const EXPORT_NOTAS = [
  { label: "Número", key: "numero" },
  { label: "Série", key: "serie" },
  { label: "Chave", key: "chave" },
  { label: "Emissão", getValue: (n) => fmtData(n.datEmissao) },
  { label: "Pedido", key: "pedido" },
  { label: "Destinatário", key: "destinatario" },
  { label: "Cidade", key: "cidadeDestino" },
  { label: "UF", key: "ufDestino" },
  { label: "Peso", getValue: (n) => (n.pesoBruto == null ? "" : String(n.pesoBruto).replace(".", ",")) },
  { label: "Valor", getValue: (n) => (n.valorTotal == null ? "" : String(n.valorTotal).replace(".", ",")) },
  { label: "Frete simulado", getValue: (n) => (n.valFreteSimulado == null ? "" : String(n.valFreteSimulado).replace(".", ",")) },
  { label: "Com documento", getValue: (n) => (n.temDocumento ? "Sim" : "Não") },
];
export default function RomaneioModal({
  idRomaneio = null,
  empresaId,
  transportadoras = [],
  podeEditar,
  onClose,
  idsNotasIniciais = [],
  idTransportadoraInicial = null,
}) {
  const [id, setId] = useState(idRomaneio);
  const [detalhe, setDetalhe] = useState(null);
  const [carregando, setCarregando] = useState(Boolean(idRomaneio));
  const [erroCarga, setErroCarga] = useState("");
  const [form, setForm] = useState(() => ({
    ...FORM_VAZIO,
    idTransportadora: !idRomaneio && idTransportadoraInicial ? String(idTransportadoraInicial) : "",
  }));
  const jaVinculouIniciais = useRef(false); // notas da tela de Pedidos: vincula só uma vez
  const [erros, setErros] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState(null); // { tom, texto }
  const [alterou, setAlterou] = useState(false);
  const [pesquisaAberta, setPesquisaAberta] = useState(false);
  const [desvinculando, setDesvinculando] = useState(false);
  const [confirmandoDesvinculo, setConfirmandoDesvinculo] = useState(false);
  const selecao = useSelecao();

  const tiposVeiculo = useLista(listarTiposVeiculo, empresaId || "sempre");
  const tiposServico = useLista(listarTiposServico);
  const modais = useLista(listarModais);
  const estados = useLista(listarEstados);
  const cidades = useLista(listarCidades, form.idEstado || null);
  const rotas = useLista(listarRotas, form.idTransportadora || null);
  const negocios = useLista(listarNegocios, empresaId || null);

  const aplicarRegistro = useCallback((r) => {
    setDetalhe(r || null);
    setForm(formDoRegistro(r));
  }, []);

  const carregar = useCallback(
    async (alvo) => {
      setCarregando(true);
      setErroCarga("");
      try {
        aplicarRegistro(await obterRomaneio(alvo));
      } catch (e) {
        setErroCarga(extractError(e, "Não foi possível carregar o romaneio."));
      } finally {
        setCarregando(false);
      }
    },
    [aplicarRegistro]
  );

  useEffect(() => {
    if (idRomaneio) carregar(idRomaneio);
  }, [idRomaneio, carregar]);

  const notas = (detalhe && detalhe.notas) || [];
  const totais = (detalhe && detalhe.totais) || {};
  const qtdNotas = notas.length || Number(totais.qtdNotas) || 0;
  const travaTransportadora = Boolean(id) && qtdNotas > 0;
  const somenteLeitura = !podeEditar;
  const bloqueado = somenteLeitura || salvando || carregando;
  const notasLivres = notas.filter((n) => !n.temDocumento);

  const set = (campo, valor) => {
    setForm((atual) => {
      const prox = { ...atual, [campo]: valor };
      if (campo === "idEstado") prox.idCidade = "";
      if (campo === "idTransportadora") prox.idRota = "";
      return prox;
    });
    setErros((atual) => (atual[campo] ? { ...atual, [campo]: undefined } : atual));
  };

  const salvar = async () => {
    if (salvando || somenteLeitura) return;
    const locais = validarForm(form);
    if (Object.keys(locais).length) {
      setErros(locais);
      const faltando = Object.keys(locais).map((k) => ROTULOS[k] || k);
      setAviso({ tom: "erro", texto: `Confira os campos: ${faltando.join(", ")}.` });
      return;
    }
    setSalvando(true);
    setErros({});
    setAviso(null);
    try {
      const corpo = payloadDoForm(form);
      const salvo = id ? await atualizarRomaneio(id, corpo) : await criarRomaneio(corpo);
      const novo = !id;
      if (salvo && salvo.id) {
        setId(salvo.id);
        aplicarRegistro(salvo);
      }
      setAlterou(true);
      const codigo = (salvo && salvo.codigo) || corpo.codigo;
      const vincularIniciais = novo && salvo && salvo.id && idsNotasIniciais.length > 0 && !jaVinculouIniciais.current;
      if (vincularIniciais) {
        jaVinculouIniciais.current = true;
        try {
          const r = await vincularNotas(salvo.id, idsNotasIniciais);
          const vinculadas = Number(r && r.vinculadas) || 0;
          const ignoradas = Array.isArray(r && r.ignoradas) ? r.ignoradas : [];
          setAviso({
            tom: ignoradas.length ? "aviso" : "ok",
            texto:
              `Romaneio ${codigo} criado. ${fmtNumber(vinculadas)} nota(s) vinculada(s)` +
              (ignoradas.length ? ` · ${fmtNumber(ignoradas.length)} ignorada(s) (já em outro romaneio ou de outra transportadora)` : "") +
              ".",
          });
        } catch (e) {
          setAviso({ tom: "erro", texto: extractError(e, "Romaneio criado, mas não foi possível vincular as notas.") });
        }
        await carregar(salvo.id);
      } else {
        setAviso({
          tom: "ok",
          texto: novo ? `Romaneio ${codigo} criado. Agora adicione as notas fiscais.` : `Romaneio ${codigo} salvo.`,
        });
      }
    } catch (e) {
      setErros(errosDoServidor(e));
      setAviso({ tom: "erro", texto: extractError(e, "Não foi possível salvar o romaneio.") });
    } finally {
      setSalvando(false);
    }
  };

  const pedirDesvinculo = () => {
    if (desvinculando || !selecao.ids.length) return;
    setAviso(null);
    setConfirmandoDesvinculo(true);
  };

  // Seleção mudou: a confirmação era das notas marcadas antes.
  const qtdSelecionadas = selecao.ids.length;
  useEffect(() => {
    setConfirmandoDesvinculo(false);
  }, [qtdSelecionadas]);

  const desvincular = async () => {
    if (desvinculando || !selecao.ids.length) return;
    setDesvinculando(true);
    setAviso(null);
    try {
      const r = await desvincularNotas(id, selecao.ids);
      const qtd = Number(r && r.desvinculadas) || 0;
      setAviso({ tom: "ok", texto: `${fmtNumber(qtd)} nota(s) desvinculada(s).` });
      selecao.limpar();
      setAlterou(true);
      await carregar(id);
    } catch (e) {
      setAviso({ tom: "erro", texto: extractError(e, "Não foi possível desvincular as notas.") });
    } finally {
      setDesvinculando(false);
      setConfirmandoDesvinculo(false);
    }
  };

  const exportarNotas = () =>
    exportCsv({ filename: `romaneio-${(detalhe && detalhe.codigo) || id}-notas`, columns: EXPORT_NOTAS, rows: notas });

  const aoVincular = async ({ vinculadas, ignoradas }) => {
    setPesquisaAberta(false);
    setAlterou(true);
    const qtdIgnoradas = ignoradas.length;
    setAviso({
      tom: qtdIgnoradas ? "aviso" : "ok",
      texto: `${fmtNumber(vinculadas)} vinculada(s), ${fmtNumber(qtdIgnoradas)} ignorada(s) (já em outro romaneio ou de outra transportadora).`,
    });
    await carregar(id);
  };

  const fechar = () => {
    if (salvando || desvinculando) return;
    onClose(alterou);
  };

  // Opção do registro quando a lista de apoio ainda não a trouxe (ex.: cidade
  // sem estado no registro, rota inativa).
  const garantirOpcao = (lista, valor, rotulo) =>
    valor && !lista.some((i) => String(i.id) === String(valor)) && rotulo ? [{ id: valor, _rotulo: rotulo }].concat(lista) : lista;

  // Romaneio antigo com tipo inativo (ex.: "Frete combinado (legado)") mantém o tipo na edição.
  const opcoesTipoVeiculo = garantirOpcao(
    tiposVeiculo,
    form.idTipoVeiculo,
    detalhe && String(detalhe.idTipoVeiculo) === form.idTipoVeiculo ? detalhe.tipoVeiculo : null
  );
  const opcoesCidade = garantirOpcao(cidades, form.idCidade, detalhe && String(detalhe.idCidade) === form.idCidade ? detalhe.cidade : null);
  const opcoesRota = garantirOpcao(rotas, form.idRota, detalhe && String(detalhe.idRota) === form.idRota ? detalhe.rota : null);
  const opcoesTransportadora = garantirOpcao(
    transportadoras,
    form.idTransportadora,
    detalhe && String(detalhe.idTransportadora) === form.idTransportadora ? detalhe.transportadora : null
  );

  const campo = (nome, label, controle, { obrig = false, dica = null } = {}) => (
    <FormGroup>
      <Label for={`romaneio-${nome}`}>
        {label}
        {obrig ? " *" : ""}
      </Label>
      {controle}
      {erros[nome] ? <span className="docs-frete__erro-campo">{erros[nome]}</span> : null}
      {dica ? <span className="docs-frete__dica">{dica}</span> : null}
    </FormGroup>
  );

  const input = (nome, props = {}) => (
    <Input
      id={`romaneio-${nome}`}
      value={form[nome]}
      onChange={(e) => set(nome, e.target.value)}
      disabled={bloqueado}
      invalid={Boolean(erros[nome])}
      style={fieldInputStyle}
      {...props}
    />
  );

  const select = (nome, lista, rotular, props = {}) => (
    <Input
      id={`romaneio-${nome}`}
      type="select"
      value={form[nome]}
      onChange={(e) => set(nome, e.target.value)}
      disabled={bloqueado}
      invalid={Boolean(erros[nome])}
      style={fieldInputStyle}
      {...props}
    >
      <option value="">Selecione...</option>
      {lista.map((i) => (
        <option key={i.id} value={i.id}>
          {i._rotulo || rotular(i)}
        </option>
      ))}
    </Input>
  );

  const codigoTitulo = (detalhe && detalhe.codigo) || (id ? `#${id}` : "");
  const titulo = !id ? "Criar romaneio" : somenteLeitura ? `Romaneio ${codigoTitulo}` : `Editar romaneio ${codigoTitulo}`;

  return (
    <>
      <ModalHub titulo={titulo} icone="tim-icons icon-delivery-fast" onClose={fechar} maxWidth={1080}>
        {erroCarga ? <Aviso tom="erro">{erroCarga}</Aviso> : null}
        {!id && idsNotasIniciais.length ? (
          <Aviso tom="info">{fmtNumber(idsNotasIniciais.length)} nota(s) selecionada(s) na tela de Pedidos serão vinculadas ao salvar.</Aviso>
        ) : null}
        {carregando && !detalhe ? (
          <EstadoTabela loading textoCarregando="Carregando o romaneio..." />
        ) : (
          <div className="docs-frete__form">
            <Row>
              <Col md="3">{campo("codigo", "Código", input("codigo", { type: "text", maxLength: 30 }), { obrig: true })}</Col>
              <Col md="3">{campo("dataSaida", "Data de embarque", input("dataSaida", { type: "date" }), { obrig: true })}</Col>
              <Col md="6">{campo("idTipoVeiculo", "Tipo de veículo", select("idTipoVeiculo", opcoesTipoVeiculo, rotuloItem), { obrig: true })}</Col>
            </Row>
            <Row>
              <Col md="6">{campo("idEstado", "Estado", select("idEstado", estados, (e) => [e.uf, e.nome].filter(Boolean).join(" - ")))}</Col>
              <Col md="6">
                {campo("idCidade", "Cidade", select("idCidade", opcoesCidade, rotuloItem), {
                  obrig: true,
                  dica: !form.idEstado && !form.idCidade ? "Escolha o estado para carregar as cidades." : null,
                })}
              </Col>
            </Row>
            <Row>
              <Col md="6">
                {campo(
                  "idTransportadora",
                  "Transportadora",
                  select("idTransportadora", opcoesTransportadora, nomeTransportadora, { disabled: bloqueado || travaTransportadora }),
                  {
                    obrig: true,
                    dica: travaTransportadora
                      ? "A transportadora não pode ser trocada enquanto houver notas vinculadas. Desvincule as notas para trocar."
                      : null,
                  }
                )}
              </Col>
              <Col md="6">
                {campo("idRota", "Rota", select("idRota", opcoesRota, rotuloRota, { disabled: bloqueado || !form.idTransportadora }), {
                  obrig: true,
                  dica: !form.idTransportadora ? "Escolha a transportadora para carregar as rotas." : null,
                })}
              </Col>
            </Row>
            <Row>
              <Col md="4">{campo("idNegocio", "Tipo de negócio", select("idNegocio", negocios, rotuloItem))}</Col>
              <Col md="4">{campo("idTipoServico", "Tipo de serviço", select("idTipoServico", tiposServico, (t) => [t.codigo, t.nome].filter(Boolean).join(" - ") || `#${t.id}`))}</Col>
              <Col md="4">
                {campo(
                  "calculo",
                  "Unidade",
  select("calculo", opcoesUnidade(detalhe && detalhe.calculo), (u) => u._rotulo)
                )}
              </Col>
            </Row>
            <Row>
              <Col md="4">{campo("tipoRomaneio", "Tipo romaneio", input("tipoRomaneio", { type: "text", maxLength: 100 }))}</Col>
              <Col md="4">{campo("idModal", "Modal", select("idModal", modais, rotuloItem))}</Col>
              <Col md="4">{campo("tipoRoma", "Tipo roma", input("tipoRoma", { type: "text", maxLength: 10 }))}</Col>
            </Row>
            <Row>
              <Col md="2">{campo("kmTotal", "Km total", input("kmTotal", { type: "text", inputMode: "decimal", placeholder: "0,0" }))}</Col>
              <Col md="2">{campo("placaVeiculo", "Placa", input("placaVeiculo", { type: "text", maxLength: 10 }))}</Col>
              <Col md="3">{campo("motorista", "Motorista", input("motorista", { type: "text", maxLength: 100 }))}</Col>
              <Col md="2">{campo("hrSaida", "Hora de saída", input("hrSaida", { type: "time" }))}</Col>
              <Col md="3">{campo("remessa", "Remessa", input("remessa", { type: "text", maxLength: 100 }))}</Col>
            </Row>
            <Row>
              <Col md="4">{campo("dtPrevColeta", "Previsão de coleta", input("dtPrevColeta", { type: "datetime-local" }))}</Col>
            </Row>
          </div>
        )}

        {aviso ? <Aviso tom={aviso.tom}>{aviso.texto}</Aviso> : null}

        {!somenteLeitura ? (
          <div className="docs-frete__acoes">
            <Button color="primary" size="sm" onClick={salvar} disabled={salvando || carregando}>
              {salvando ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
              {salvando ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        ) : null}

        {id && detalhe ? (
          <>
            <div className="docs-frete__barra" style={{ marginTop: "1rem" }}>
              <p className="docs-frete__secao" style={{ margin: 0 }}>
                Notas fiscais · {fmtNumber(notas.length)}
              </p>
              <div className="docs-frete__acoes">
                <Button color="default" size="sm" className="btn-simple" onClick={exportarNotas} disabled={!notas.length} title="Exportar as notas do romaneio">
                  Exportar notas
                </Button>
                {!somenteLeitura ? (
                  <Button color="primary" size="sm" className="btn-simple" onClick={() => setPesquisaAberta(true)} disabled={desvinculando}>
                    Adicionar notas
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="docs-frete__totais" role="region" aria-label="Totais do romaneio">
              <span>
                Qtd. NF <b>{fmtNumber(totais.qtdNotas != null ? totais.qtdNotas : notas.length)}</b>
              </span>
              <span>
                Valor total NF <b>{fmtBRL(totais.valorTotalNotas)}</b>
              </span>
              <span>
                Peso total <b>{fmtPeso(totais.pesoTotal)}</b>
              </span>
              <span>
                Frete simulado <b>{fmtBRL(totais.freteSimulado)}</b>
              </span>
            </div>
            {!somenteLeitura && selecao.totais.qtd > 0 ? (
              confirmandoDesvinculo ? (
                <Aviso tom="aviso" role="alertdialog">
                  Desvincular {fmtNumber(selecao.ids.length)} nota(s) selecionada(s) do romaneio?
                  <div className="docs-frete__acoes">
                    <Button color="danger" size="sm" onClick={desvincular} disabled={desvinculando}>
                      {desvinculando ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                      {desvinculando ? "Desvinculando..." : "Confirmar desvínculo"}
                    </Button>
                    <Button color="default" size="sm" outline onClick={() => setConfirmandoDesvinculo(false)} disabled={desvinculando}>
                      Voltar
                    </Button>
                  </div>
                </Aviso>
              ) : (
                <SelecaoResumo totais={selecao.totais}>
                  <Button color="danger" size="sm" onClick={pedirDesvinculo} disabled={desvinculando}>
                    Desvincular
                  </Button>
                  <Button color="default" size="sm" outline onClick={selecao.limpar} disabled={desvinculando}>
                    Desmarcar seleção
                  </Button>
                </SelecaoResumo>
              )
            ) : null}
            <EstadoTabela
              loading={carregando}
              vazio={!carregando && !notas.length}
              textoCarregando="Carregando as notas..."
              textoVazio={somenteLeitura ? "Nenhuma nota vinculada." : 'Nenhuma nota vinculada. Use "Adicionar notas".'}
            />
            {!carregando && notas.length ? (
              <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive hover>
                <thead className="text-primary">
                  <tr>
                    {!somenteLeitura ? (
                      <th className="is-sel">
                        <CheckboxTodos itens={notasLivres} selecao={selecao} label="Selecionar todas as notas sem documento" />
                      </th>
                    ) : null}
                    <th>Número/Série</th>
                    <th>Chave</th>
                    <th>Emissão</th>
                    <th>Pedido</th>
                    <th>Destinatário</th>
                    <th>Cidade/UF</th>
                    <th className="is-number">Peso</th>
                    <th className="is-number">Valor</th>
                    <th className="is-number">Frete simulado</th>
                  </tr>
                </thead>
                <tbody>
                  {notas.map((n) => (
                    <tr key={n.id}>
                      {!somenteLeitura ? (
                        <td className="is-sel">
                          <input
                            type="checkbox"
                            aria-label={`Selecionar nota ${n.numero}`}
                            title={n.temDocumento ? "Nota com CT-e ou NFse não pode ser desvinculada" : undefined}
                            checked={selecao.has(n.id)}
                            disabled={Boolean(n.temDocumento)}
                            onChange={() => selecao.toggle(n)}
                          />
                        </td>
                      ) : null}
                      <td>
                        <strong>{n.numero}</strong>
                        <small>Série {n.serie || "-"}</small>
                        {n.temDocumento ? <Pill tom="success">Com documento</Pill> : null}
                      </td>
                      <td className="docs-frete__chave">{n.chave || "-"}</td>
                      <td className="docs-frete__nowrap">{fmtData(n.datEmissao)}</td>
                      <td>{n.pedido || "-"}</td>
                      <td>{n.destinatario || "-"}</td>
                      <td>{[n.cidadeDestino, n.ufDestino].filter(Boolean).join("/") || "-"}</td>
                      <td className="is-number">{fmtPeso(n.pesoBruto)}</td>
                      <td className="is-number">{fmtBRL(n.valorTotal)}</td>
                      <td className="is-number">{fmtBRL(n.valFreteSimulado)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : null}
          </>
        ) : null}

        {!id && !somenteLeitura ? <Aviso tom="info">Salve o romaneio para vincular as notas fiscais.</Aviso> : null}

        <div className="docs-frete__modal-rodape">
          <Button color="secondary" size="sm" onClick={fechar} disabled={salvando || desvinculando}>
            Fechar
          </Button>
        </div>
      </ModalHub>

      {pesquisaAberta && detalhe ? (
        <PesquisaNotasModal romaneio={detalhe} onClose={() => setPesquisaAberta(false)} onVinculado={aoVincular} />
      ) : null}
    </>
  );
}
